import { useState } from 'react';
import { motion } from 'motion/react';
import { X, Search, Send, UserPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useMessaging } from './MessagingContext';
import { useUserData } from './UserDataContext';
import { EmbeddedYap } from './EmbeddedYap';
import { getYapInteractionId, getOriginalYapId } from './utils/yapUtils';

interface ShareShoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  shout: any; // The shouted yap from the shouter's perspective
}

export function ShareShoutModal({ isOpen, onClose, shout }: ShareShoutModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<number[]>([]);
  const [messageText, setMessageText] = useState('');
  const [showFriendsList, setShowFriendsList] = useState(false);
  const { createOrUpdateChat, addMessage } = useMessaging();
  const { userProfile, friends } = useUserData();

  if (!shout) return null;

  // Note: Friend user data should be fetched from Firestore via UserService.fetchUserById()
  // For now using empty array until integrated
  const userFriendsList: Array<{ id: number; name: string; username: string; avatar: string }> = [];

  const filteredFriends = userFriendsList.filter(
    (friend) =>
      friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      friend.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFriend = (friendId: number) => {
    setSelectedFriends((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleSend = () => {
    if (selectedFriends.length === 0) {
      toast.error('Please select at least one friend');
      return;
    }

    // Validate shout data
    if (!shout) {
      toast.error('Invalid shout data');
      return;
    }

    // Send the shout to each selected friend
    selectedFriends.forEach(friendId => {
      const friend = userFriendsList.find(f => f.id === friendId);
      if (!friend) return;

      // Create or update chat for this friend
      const now = new Date();
      const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

      // When sharing a shout, we're sharing OUR shout (with our caption/images)
      // The shout object contains: shout.author (us), shout.shoutCaption (our caption), shout.originalYap (original post)
      const shoutContent = shout.shoutCaption || shout.originalYap?.content || shout.content || '';
      const contentPreview = shoutContent.substring(0, 50);
      const lastMessageText = messageText.trim()
        ? messageText.trim()
        : `Shared a shout: ${contentPreview}${shoutContent.length > 50 ? '...' : ''}`;

      createOrUpdateChat({
        id: friendId,
        name: friend.name,
        avatar: friend.avatar,
        lastMessage: lastMessageText,
        time: timeStr,
        unread: 0,
        online: false,
        username: friend.username,
      });

      // Add the shared shout as a message with optional text
      // When sharing a shouted yap, show it from the SHOUTER's perspective (the person sharing = current user)
      // The author should be the SHOUTER (us), not the original yap author
      addMessage(friendId, {
        id: `msg_${Date.now()}_${friendId}`,
        sender: 'user',
        text: messageText.trim() || `Shared a shout`,
        time: timeStr,
        timestamp: now.getTime(),
        sharedYap: {
          id: shout.id || `shout_${Date.now()}`,
          // IMPORTANT: Show the SHOUTER as the author (the person sharing - current user)
          author: userProfile.name,
          username: userProfile.username,
          avatar: userProfile.avatar,
          // Show the caption if it exists, otherwise show original content
          content: shout.shoutCaption || shout.originalYap?.content || '',
          time: shout.timestamp,
          // Don't include original yap stats for shared shouts
          likes: 0,
          comments: 0,
          shouts: 0,
          shoutedBy: shout.shoutedBy, // UID
          shoutedByUsername: shout.shoutedByUsername, // @username
          shoutedByName: shout.shoutedByName,
          shoutedByAvatar: shout.shoutedByAvatar,
          shoutCaption: shout.shoutCaption,
          shoutImages: shout.shoutImages,
          type: 'shouted' as const,
          image: shout.shoutImages?.[0] || shout.images?.[0] || shout.image,
          // Store the original yap reference for context
          originalYap: shout.originalYap ? {
            id: shout.originalYap.id,
            author: shout.originalYap.author,
            username: shout.originalYap.username,
            content: shout.originalYap.content,
          } : undefined,
        },
      });
    });

    toast.success(`Shout shared with ${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}!`);
    setSelectedFriends([]);
    setSearchQuery('');
    setMessageText('');
    onClose();
  };

  // Determine what to display in the preview
  // Show the shout from the perspective of the SHOUTER (current user)
  const previewContent = shout.shoutCaption || shout.originalYap?.content || shout.content || '';
  const hasCaption = Boolean(shout.shoutCaption);
  const hasShoutImages = Boolean(shout.shoutImages && shout.shoutImages.length > 0);
  const originalAuthor = shout.originalYap?.author || shout.originalYap?.userName || 'Unknown';

  // Get shouter information
  const shouterName = shout.shoutedByName || userProfile.name;
  const shouterUsername = shout.shoutedByUsername || shout.shoutedBy || userProfile.username;
  const shouterAvatar = shout.shoutedByAvatar || userProfile.avatar;

  // Get original yap username for display
  const originalUsername = shout.originalYap?.username || shout.originalYap?.userUsername || '@unknown';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 bg-[#e7d299] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 overflow-hidden">
        <DialogTitle className="sr-only">Share Shout with Friends</DialogTitle>
        <DialogDescription className="sr-only">
          Select friends to share this shout with. You can search for friends and select multiple recipients.
        </DialogDescription>

        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-black dark:text-white">Share Shout</h2>
            <button
              onClick={onClose}
              className="text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Shout Preview */}
        <div className="px-4 py-3 bg-[#F5F5F5] dark:bg-[#2a2a2a] border-b border-black/10 dark:border-white/10">
          {/* Shouter Info (You) */}
          <div className="flex items-start gap-3 mb-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={shouterAvatar} />
              <AvatarFallback>{shouterName[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm text-black dark:text-white">{shouterName}</p>
                <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">{shouterUsername}</p>
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#F39C12]/20 dark:bg-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea]">
                  📢 Shouted
                </span>
              </div>
              {/* Show shout caption if it exists */}
              {hasCaption && (
                <p className="text-sm text-black dark:text-[#E5E5E5] mt-1">
                  {shout.shoutCaption}
                </p>
              )}
              {/* Show shout images if they exist */}
              {hasShoutImages && (
                <div className="mt-2 space-y-1">
                  {shout.shoutImages.slice(0, 2).map((img: string, idx: number) => (
                    <img key={idx} src={img} alt="Shout" className="rounded-lg w-full h-20 object-cover" />
                  ))}
                  {shout.shoutImages.length > 2 && (
                    <p className="text-xs text-[#999999]">+{shout.shoutImages.length - 2} more</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Original Yap Preview */}
          {shout.originalYap && (
            <div className="ml-13">
              <EmbeddedYap
                yapId={getOriginalYapId(shout.originalYap)}
                username={originalUsername}
                authorName={shout.originalYap.author || shout.originalYap.userName || 'Unknown'}
                avatar={shout.originalYap.avatar || shout.originalYap.userAvatar || ''}
                timestamp={shout.originalYap.timestamp || shout.originalYap.time || ''}
                content={shout.originalYap.content || ''}
                image={shout.originalYap.image}
                likes={shout.originalYap.likes || 0}
                comments={shout.originalYap.replies || shout.originalYap.comments || 0}
                shouts={shout.originalYap.shouts || 0}
                isDeleted={shout.originalYap.deleted}
                onYapClick={() => { }}
                maxLines={2}
                showActions={false}
              />
            </div>
          )}
        </div>

        {/* Select Friends Section */}
        <div className="px-4 pt-3 pb-2">
          <div className="mb-2">
            <p className="text-sm text-black dark:text-white mb-2">Select Friends</p>
            {/* Search Bar */}
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]" />
              <Input
                placeholder="Search friends..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 bg-[#F5F5F5] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-black dark:text-white"
              />
            </div>
          </div>

          {/* Scrollable Friends List */}
          <ScrollArea className="h-[180px] border border-black/10 dark:border-white/10 rounded-xl">
            {userFriendsList.length === 0 ? (
              // Empty state when no friends
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <UserPlus className="w-12 h-12 text-[#999999] dark:text-[#666666] mb-3" />
                <p className="text-sm text-black dark:text-white mb-1">Add more friends to send yaps to</p>
                <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">
                  You haven't added any friends yet
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onClose();
                    window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'discover' }));
                  }}
                  className="mt-4 px-4 py-2 rounded-xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white text-sm"
                >
                  Find Friends
                </motion.button>
              </div>
            ) : filteredFriends.length === 0 ? (
              // Empty state when search has no results
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <Search className="w-12 h-12 text-[#999999] dark:text-[#666666] mb-3" />
                <p className="text-sm text-black dark:text-white mb-1">No friends found</p>
                <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">
                  Try a different search term
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredFriends.map((friend) => (
                  <motion.div
                    key={friend.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => toggleFriend(friend.id)}
                    className={`p-2 rounded-lg cursor-pointer transition-all ${selectedFriends.includes(friend.id)
                      ? 'bg-[#F39C12]/20 border border-[#F39C12]'
                      : 'bg-white dark:bg-[#1a1a1a] border border-transparent hover:bg-[#F5F5F5] dark:hover:bg-[#2a2a2a]'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src={friend.avatar} />
                        <AvatarFallback>{friend.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-black dark:text-white truncate">{friend.name}</p>
                        <p className="text-xs text-[#666666] dark:text-[#a0a0a0] truncate">{friend.username}</p>
                      </div>
                      {selectedFriends.includes(friend.id) && (
                        <div className="w-5 h-5 rounded-full bg-[#F39C12] flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Message Text Area */}
        <div className="px-4 pt-2 pb-3">
          <Textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Include a message..."
            className="min-h-[80px] bg-[#F5F5F5] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-black dark:text-white resize-none rounded-xl"
            maxLength={500}
          />
          <p className="text-xs text-[#999999] dark:text-[#808080] mt-1 text-right">
            {messageText.length}/500
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-black/10 dark:border-white/10 bg-[#F5F5F5] dark:bg-[#2a2a2a]">
          <Button
            onClick={handleSend}
            disabled={selectedFriends.length === 0}
            className="w-full h-12 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl"
          >
            <Send className="w-5 h-5 mr-2" />
            Send to {selectedFriends.length} friend{selectedFriends.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
